import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import {
  Shield,
  Eye,
  Zap,
  TrendingUp,
  Users,
  ArrowRight,
  Play,
  Calculator,
  CheckCircle,
  Star,
} from 'lucide-react';

interface HeroSectionProps {
  onDemoRequest?: () => void;
  onCalculatorOpen?: () => void;
  onVideoPlay?: () => void;
  variant?: 'default' | 'problem-focused' | 'solution-focused' | 'outcome-focused';
}

export function GovernanceHeroSection({
  onDemoRequest,
  onCalculatorOpen,
  onVideoPlay,
  variant = 'solution-focused',
}: HeroSectionProps) {
  const navigate = useNavigate();
  const [counters, setCounters] = useState({
    targetDecisionDays: 0,
    spendingTransparency: 0,
    preventUnauthorized: 0,
    digitalAccess: 0,
  });

  // Animate counters on component mount
  useEffect(() => {
    const animateCounters = () => {
      const targets = {
        targetDecisionDays: 7, // Target: decisions in 7 days or less
        spendingTransparency: 100, // 100% spending transparency capability
        preventUnauthorized: 100, // 100% prevention of unauthorized spending
        digitalAccess: 24, // 24/7 digital access to all information
      };

      Object.keys(targets).forEach((key) => {
        const target = targets[key as keyof typeof targets];
        let current = 0;
        const increment = target / 50; // 50 steps for smooth animation

        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }

          setCounters((prev) => ({
            ...prev,
            [key]: Math.floor(current),
          }));
        }, 40); // 40ms per step = 2 second total animation
      });
    };

    // Start animation after component mounts
    const timeout = setTimeout(animateCounters, 1000);
    return () => clearTimeout(timeout);
  }, []);

  const getHeadlineContent = () => {
    switch (variant) {
      case 'problem-focused':
        return {
          headline: "Tired of HOA Board Drama? There's a Better Way.",
          subheadline:
            "73% of HOAs experience major conflicts annually. We're building a digital governance system that eliminates the politics and gets results.",
        };
      case 'outcome-focused':
        return {
          headline: 'From 6-Month Delays to Same-Week Decisions',
          subheadline:
            'Traditional HOAs average 180 days per decision. Our system is designed to cut that to under 7 days while maintaining full transparency.',
        };
      case 'solution-focused':
      default:
        return {
          headline: 'Finally, Governance That Works During Conflicts',
          subheadline:
            "While 73% of HOAs and landowners associations struggle with disputes that paralyze decision making, we're building a conflict proof system that keeps communities moving forward.",
        };
    }
  };

  const { headline, subheadline } = getHeadlineContent();

  const keyBenefits = [
    {
      icon: Zap,
      title: 'Get Things Done Fast',
      description: 'No more waiting months for simple decisions',
      color: 'text-blue-600 bg-blue-100 border-blue-200',
    },
    {
      icon: Eye,
      title: 'See Where Your Money Goes',
      description: 'Every expense tracked - no more financial surprises',
      color: 'text-green-600 bg-green-100 border-green-200',
    },
    {
      icon: Shield,
      title: 'No More Board Drama',
      description: 'Fair system that works even when people disagree',
      color: 'text-purple-600 bg-purple-100 border-purple-200',
    },
    {
      icon: Shield,
      title: 'Peace of Mind',
      description: 'Sleep better knowing your community is protected',
      color: 'text-orange-600 bg-orange-100 border-orange-200',
    },
  ];

  return (
    <div className="relative overflow-hidden bg-linear-to-br from-blue-50 via-white to-purple-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' width='32' height='32' fill='none' stroke='%23000'%3e%3cpath d='m0 2 l30 30 m2 -30 l30 30' stroke-width='0.5'/%3e%3c/svg%3e")`,
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto sm:px-6 lg:px-8 py-12 xs:py-16 sm:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-8 xs:gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-6 xs:space-y-8">
            {/* Trust Badge */}
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-2 xs:px-3 py-1 hover:bg-blue-100 hover:text-blue-800 text-xs xs:text-sm">
                <Star className="w-3 h-3 mr-1" />
                <span className="hidden xs:inline">Ready to Help Your Community</span>
                <span className="xs:hidden">Ready to Help</span>
              </Badge>
            </div>

            {/* Main Headlines */}
            <div className="space-y-3 xs:space-y-4">
              <h1 className="text-2xl xs:text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                <span className="bg-linear-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                  {headline}
                </span>
              </h1>

              <p className="text-base xs:text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                {subheadline}
              </p>
            </div>

            {/* Key Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 xs:gap-4">
              {keyBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`p-3 xs:p-4 rounded-lg border ${benefit.color} transition-all duration-300 hover:shadow-md hover:-translate-y-1`}
                >
                  <div className="flex items-start gap-2 xs:gap-3">
                    <div className={`p-1.5 xs:p-2 rounded-lg ${benefit.color} shrink-0`}>
                      <benefit.icon className="w-4 h-4 xs:w-5 xs:h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-xs xs:text-sm">{benefit.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1 leading-tight">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Call-to-Action Buttons */}
            <div className="flex flex-col gap-3 xs:gap-4 relative z-10">
              <Button
                size="lg"
                className="bg-linear-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 xs:px-8 py-3 xs:py-4 text-base xs:text-lg font-semibold w-full sm:w-auto"
                onClick={() => (onDemoRequest ? onDemoRequest() : navigate('/schedule-demo'))}
              >
                <span className="hidden xs:inline">See How This Could Work in Your Community</span>
                <span className="xs:hidden">See How This Works</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="border-2 border-blue-200 hover:bg-blue-50 px-6 xs:px-8 py-3 xs:py-4 text-base xs:text-lg font-semibold w-full sm:w-auto"
                onClick={onCalculatorOpen}
              >
                <Calculator className="w-4 h-4 xs:w-5 xs:h-5 mr-2" />
                <span className="hidden xs:inline">Calculate Your Community's Savings</span>
                <span className="xs:hidden">Calculate Savings</span>
              </Button>
            </div>

            {/* Development Status */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>System ready for communities</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Solving real HOA problems</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Built by community experts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>No technical skills required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Works on any device</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <span>Accepting pilot communities</span>
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="space-y-8">
            {/* Industry Problems & Solution Dashboard */}
            <Card className="shadow-2xl border-0 bg-white/80 backdrop-blur-xs">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  <div className="flex items-center justify-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      What Our System is Built to Do
                    </h3>
                    <InfoTooltip
                      title="System Design Capabilities"
                      description="These represent the core capabilities and targets our digital governance system is specifically designed to achieve, based on common HOA problems we're solving."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-blue-600">
                        {counters.targetDecisionDays}
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <div className="text-sm text-muted-foreground">
                          Target Days for Decisions
                        </div>
                        <InfoTooltip
                          title="Designed for Speed"
                          description="Our system is built to enable routine governance decisions in 7 days or less through streamlined digital processes, automated workflows, and clear approval chains."
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-green-600">
                        {counters.spendingTransparency}%
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <div className="text-sm text-muted-foreground">Spending Transparency</div>
                        <InfoTooltip
                          title="Complete Financial Visibility"
                          description="Every dollar spent is tracked, approved digitally, and visible to all residents in real-time. No more unauthorized spending or financial surprises - exactly what your community needs after recent issues."
                          side="left"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-purple-600">
                        {counters.preventUnauthorized}%
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <div className="text-sm text-muted-foreground">
                          Prevent Unauthorized Actions
                        </div>
                        <InfoTooltip
                          title="Built-in Safeguards"
                          description="Digital approval chains, spending limits, and multi-person authorization requirements make unauthorized board actions impossible."
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-3xl font-bold text-orange-600">
                        {counters.digitalAccess}/7
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <div className="text-sm text-muted-foreground">
                          Digital Access Availability
                        </div>
                        <InfoTooltip
                          title="Always-On Transparency"
                          description="Residents can access all governance information, decisions, finances, and voting 24 hours a day, 7 days a week from any device. No more waiting for monthly meetings or wondering what's happening."
                          side="left"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t bg-blue-50 rounded-lg p-4 -mx-4">
                    <div className="text-sm text-blue-800 font-medium mb-2">
                      ✨ System Ready for Implementation
                    </div>
                    <div className="text-xs text-blue-700 mb-3">
                      Digital governance system built with these exact capabilities
                    </div>
                    {/* <Button 
                      variant="ghost" 
                      className="w-full flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
                      onClick={onVideoPlay}
                    >
                      <Play className="w-4 h-4" />
                      See How We're Solving This (5 min)
                    </Button> */}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Visual Comparison Preview - Repositioned for better visibility */}
            <div className="relative z-20 mt-8">
              <Card className="shadow-xl border-0 bg-linear-to-r from-red-50 to-green-50">
                <CardContent className="pl-2 pr-4 pt-4 pb-4">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      Why Communities Are Making the Switch
                    </h3>
                    <p className="text-sm text-gray-600">See what makes our approach different</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center space-y-4 p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                        <Users className="w-8 h-8 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-800 text-base mb-2">
                          Your Current HOA
                        </h4>
                        <div className="space-y-1 text-sm text-red-700 text-left">
                          <p className="ml-2">• Endless arguments and politics</p>
                          <p className="ml-2">• Decisions take months</p>
                          <p className="ml-2">• Hidden spending and surprises</p>
                          <p className="ml-2">• Board members with too much power</p>
                        </div>
                      </div>
                    </div>

                    <div className="text-center space-y-4 p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                        <Shield className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800 text-base mb-2">
                          Our Digital System
                        </h4>
                        <div className="space-y-1 text-sm text-green-700 text-left">
                          <p className="ml-2">• Fair process, no politics</p>
                          <p className="ml-2">• Fast decisions in days, not months</p>
                          <p className="ml-2">• Every dollar tracked and visible</p>
                          <p className="ml-2">• Built-in checks and balances</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
